import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight, ArrowDownLeft, Loader2, Clock, AlertTriangle,
  Bell, Plus, Trash2, Send, ChevronDown, Check, X, IndianRupee,
  FileText, Calendar, Settings2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

function AgingBar({ aging }) {
  const buckets = [
    { key: "current", label: "Current", color: "bg-emerald-500", textColor: "text-emerald-700" },
    { key: "days_30", label: "1-30 days", color: "bg-amber-400", textColor: "text-amber-700" },
    { key: "days_60", label: "31-60 days", color: "bg-orange-500", textColor: "text-orange-700" },
    { key: "days_90", label: "61-90 days", color: "bg-red-400", textColor: "text-red-700" },
    { key: "days_90_plus", label: "90+ days", color: "bg-red-600", textColor: "text-red-800" },
  ];
  const total = buckets.reduce((s, b) => s + (aging[b.key]?.total || 0), 0);
  if (total === 0) return null;

  return (
    <div>
      <div className="flex rounded-full overflow-hidden h-3 mb-3">
        {buckets.map(b => {
          const pct = total > 0 ? (aging[b.key]?.total || 0) / total * 100 : 0;
          return pct > 0 ? <div key={b.key} className={`${b.color} transition-all`} style={{ width: `${pct}%` }} title={`${b.label}: ${fmt(aging[b.key]?.total)}`} /> : null;
        })}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {buckets.map(b => (
          <div key={b.key} className="flex items-center gap-1.5 text-xs">
            <div className={`w-2.5 h-2.5 rounded-full ${b.color}`} />
            <span className="text-slate-500">{b.label}:</span>
            <span className={`font-semibold ${b.textColor}`}>{fmt(aging[b.key]?.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvoiceList({ invoices, onSendReminder, sending }) {
  const navigate = useNavigate();
  if (!invoices || invoices.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No invoices in this bucket</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs text-slate-500">
            <th className="text-left py-2 font-medium">Invoice</th>
            <th className="text-left py-2 font-medium">Customer</th>
            <th className="text-left py-2 font-medium">Due Date</th>
            <th className="text-right py-2 font-medium">Outstanding</th>
            <th className="text-right py-2 font-medium">Overdue</th>
            <th className="text-right py-2 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.invoice_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
              <td className="py-2.5 font-mono text-xs text-slate-700">{inv.invoice_number}</td>
              <td className="py-2.5 font-medium text-slate-900">{inv.customer_name}</td>
              <td className="py-2.5 text-slate-500">{inv.due_date || "—"}</td>
              <td className="py-2.5 text-right font-semibold text-slate-900">{fmt(inv.outstanding)}</td>
              <td className="py-2.5 text-right">
                {inv.days_overdue > 0 ? (
                  <span className="text-xs font-bold text-red-600">{inv.days_overdue}d</span>
                ) : (
                  <span className="text-xs text-emerald-600 font-medium">On time</span>
                )}
              </td>
              <td className="py-2.5 text-right">
                <button
                  onClick={(e) => { e.stopPropagation(); onSendReminder(inv.invoice_id); }}
                  className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                  title="Send reminder"
                  data-testid={`send-reminder-${inv.invoice_id}`}
                  disabled={sending === inv.invoice_id}
                >
                  {sending === inv.invoice_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReceivablesPage() {
  const [tab, setTab] = useState("receivables");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("all");
  const [expandBucket, setExpandBucket] = useState("days_30");
  const [sending, setSending] = useState(null);

  // Reminder rules
  const [rules, setRules] = useState([]);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: "", trigger: "before_due", days: 3, email_enabled: true, notification_enabled: true });

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true }).then(r => setCompanies(r.data.companies || [])).catch(() => {});
    fetchRules();
  }, []);

  useEffect(() => { fetchData(); }, [companyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = companyId !== "all" ? { company_id: companyId } : {};
      const res = await axios.get(`${API}/receivables/dashboard`, { params, withCredentials: true });
      setData(res.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/receivables/reminders/rules`, { withCredentials: true });
      setRules(res.data.rules || []);
    } catch {}
  };

  const sendReminder = async (invoiceId) => {
    setSending(invoiceId);
    try {
      const res = await axios.post(`${API}/receivables/reminders/send/${invoiceId}`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch { toast.error("Failed to send reminder"); }
    finally { setSending(null); }
  };

  const saveRule = async () => {
    if (!ruleForm.name.trim()) { toast.error("Rule name required"); return; }
    try {
      await axios.post(`${API}/receivables/reminders/rules`, ruleForm, { withCredentials: true });
      toast.success("Reminder rule added");
      setShowRuleForm(false);
      setRuleForm({ name: "", trigger: "before_due", days: 3, email_enabled: true, notification_enabled: true });
      fetchRules();
    } catch { toast.error("Failed to add rule"); }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/receivables/reminders/rules/${ruleId}`, { withCredentials: true });
      toast.success("Rule deleted");
      fetchRules();
    } catch { toast.error("Failed to delete rule"); }
  };

  const checkReminders = async () => {
    try {
      const res = await axios.post(`${API}/receivables/reminders/check`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch { toast.error("Failed to check reminders"); }
  };

  const current = tab === "receivables" ? data?.receivables : data?.payables;
  const allInvoices = current ? Object.values(current.aging).flatMap(b => b.invoices) : [];
  const bucketKeys = ["current", "days_30", "days_60", "days_90", "days_90_plus"];
  const bucketLabels = { current: "Current", days_30: "1-30 Days", days_60: "31-60 Days", days_90: "61-90 Days", days_90_plus: "90+ Days" };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="receivables-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Receivables & Payables</h1>
            <p className="text-sm text-slate-500">Track outstanding payments and manage reminders</p>
          </div>
          <div className="flex gap-2">
            {companies.length > 1 && (
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="w-48" data-testid="filter-company"><SelectValue placeholder="All Companies" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" onClick={checkReminders} className="text-sm" data-testid="check-reminders-btn">
              <Bell className="w-4 h-4 mr-1" /> Check Reminders
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "receivables", label: "Receivables", sub: "Money owed to you", icon: ArrowDownLeft },
            { id: "payables", label: "Payables", sub: "Money you owe", icon: ArrowUpRight },
            { id: "reminders", label: "Reminder Rules", sub: "Auto-reminders", icon: Settings2 },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                tab === t.id ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-100 hover:bg-slate-50"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              <span className="font-bold">{t.label}</span>
            </button>
          ))}
        </div>

        {loading && tab !== "reminders" ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : tab === "reminders" ? (
          /* ===== REMINDER RULES TAB ===== */
          <div className="space-y-4" data-testid="reminders-tab">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Auto Reminder Rules</h2>
              <Button onClick={() => setShowRuleForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="add-rule-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Rule
              </Button>
            </div>

            <AnimatePresence>
              {showRuleForm && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="rule-form">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Rule Name</label>
                      <Input value={ruleForm.name} onChange={e => setRuleForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. 3 days before due" data-testid="rule-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Trigger</label>
                      <Select value={ruleForm.trigger} onValueChange={v => setRuleForm(p => ({ ...p, trigger: v }))}>
                        <SelectTrigger data-testid="rule-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="before_due">Before due date</SelectItem>
                          <SelectItem value="on_due">On due date</SelectItem>
                          <SelectItem value="after_due">After due date</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Days</label>
                      <Input type="number" value={ruleForm.days} onChange={e => setRuleForm(p => ({ ...p, days: parseInt(e.target.value) || 0 }))} data-testid="rule-days" />
                    </div>
                    <div className="flex items-end gap-2">
                      <Button onClick={saveRule} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-rule-btn">
                        <Check className="w-4 h-4 mr-1" /> Save
                      </Button>
                      <Button variant="ghost" onClick={() => setShowRuleForm(false)}>Cancel</Button>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-3">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={ruleForm.email_enabled} onChange={e => setRuleForm(p => ({ ...p, email_enabled: e.target.checked }))} className="rounded" />
                      Send email
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={ruleForm.notification_enabled} onChange={e => setRuleForm(p => ({ ...p, notification_enabled: e.target.checked }))} className="rounded" />
                      In-app notification
                    </label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {rules.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
                <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="font-semibold text-slate-900 mb-1">No reminder rules yet</p>
                <p className="text-sm text-slate-500 mb-4">Set up auto-reminders to never miss a payment follow-up.</p>
                <Button onClick={() => setShowRuleForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-1" /> Create First Rule
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {rules.map(rule => (
                  <div key={rule.rule_id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-center justify-between group" data-testid={`rule-${rule.rule_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Bell className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{rule.name}</p>
                        <p className="text-xs text-slate-500">
                          {rule.trigger === "before_due" ? `${rule.days} days before due` : rule.trigger === "on_due" ? "On due date" : `${rule.days} days after due`}
                          {rule.email_enabled && " • Email"}
                          {rule.notification_enabled && " • Notification"}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => deleteRule(rule.rule_id)} className="p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : current ? (
          /* ===== AR or AP TAB ===== */
          <div className="space-y-6" data-testid={`${tab}-tab`}>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Total Outstanding</p>
                <p className="text-2xl font-bold text-slate-900">{fmt(current.total_outstanding)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Unpaid Invoices</p>
                <p className="text-2xl font-bold text-slate-900">{current.total_invoices}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{current.overdue_count}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Top Party</p>
                <p className="text-sm font-bold text-slate-900 truncate">{current.top_parties[0]?.name || "—"}</p>
                <p className="text-xs text-slate-500">{current.top_parties[0] ? fmt(current.top_parties[0].amount) : ""}</p>
              </div>
            </div>

            {/* Aging Bar */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Aging Analysis</h3>
              <AgingBar aging={current.aging} />
            </div>

            {/* Aging Buckets */}
            <div className="space-y-3">
              {bucketKeys.map(key => {
                const bucket = current.aging[key];
                if (!bucket || bucket.invoices.length === 0) return null;
                const isOpen = expandBucket === key;
                return (
                  <div key={key} className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <button onClick={() => setExpandBucket(isOpen ? null : key)} className="w-full flex items-center justify-between p-5" data-testid={`bucket-${key}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-900">{bucketLabels[key]}</span>
                        <span className="text-xs text-slate-500">{bucket.invoices.length} invoice{bucket.invoices.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-slate-900">{fmt(bucket.total)}</span>
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <InvoiceList invoices={bucket.invoices} onSendReminder={sendReminder} sending={sending} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Top Parties */}
            {current.top_parties.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Top {tab === "receivables" ? "Debtors" : "Creditors"}
                </h3>
                <div className="space-y-3">
                  {current.top_parties.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">{p.name}</span>
                      <span className="text-sm font-bold text-slate-900">{fmt(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
